import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import Mosaico from '../components/cameras/Mosaico';
import CameraUnica from '../components/cameras/CameraUnica';
import ListaCameras from '../components/cameras/ListaCameras';
import RecortesModal from '../components/cameras/RecortesModal';
import ConfigCameras from '../components/cameras/ConfigCameras';
import MapaCameras from '../components/cameras/MapaCameras';
import useStatusCameras from '../components/cameras/useStatusCameras';
import {
  fontesDoCatalogo, LAYOUT_AUTO, LAYOUTS, montarTelas,
} from '../helpers/camerasLayout';

// /ti/cameras — exibicao das cameras (gravacao fica no DVR).
//
// "Ver" escolhe a FONTE do tour: todos os equipamentos, um tour personalizado,
// um grupo (Caixas, Deposito...) ou uma filial. O layout pode ser o da fonte
// ("Automático") ou um fixo. A pagina mostra uma tela por vez e troca sozinha
// a cada INTERVALO segundos, para sempre. Clicar numa camera abre ela sozinha
// em HD.
//
// A fonte vai na URL (?ver=tour:3): cada TV abre direto no seu tour.
//
// Modo MAPA (?mapa=<id>): a planta / foto aerea com as cameras no lugar e a
// cor do status de cada uma. O tour fica parado enquanto isso.

const INTERVALOS = [10, 15, 20, 30, 45, 60, 120];
const INTERVALO_PADRAO = 20;
const CHAVE_INTERVALO = 'ti_cameras_intervalo';
const CHAVE_FONTE = 'ti_cameras_fonte';
const CHAVE_LAYOUT = 'ti_cameras_layout';

// Segundos antes da troca em que a PROXIMA tela ja comeca a conectar,
// escondida atras da atual. Sem isso cada troca abriria com alguns segundos
// de mosaico preto ate o video chegar.
const PRE_CARGA_S = 4;

const RECARREGAR_MS = 60000;
// Os mapas mudam pouco; a URL da imagem leva o token, que a sessao renova.
const RECARREGAR_MAPAS_MS = 5 * 60 * 1000;
// O token dura 1 dia e o tour roda em TV por semanas: renova a sessao antes
// de vencer, senao o video para e a pagina cai no login.
const RENOVAR_SESSAO_MS = 6 * 60 * 60 * 1000;

function lerLocal(chave, padrao) {
  try { return localStorage.getItem(chave) || padrao; } catch { return padrao; }
}

function gravarLocal(chave, valor) {
  try { localStorage.setItem(chave, String(valor)); } catch { /* ignore */ }
}

export default function Cameras() {
  const { temPermissao, refreshSession } = useAuth();
  const podeConfigurar = temPermissao('admin:cameras');
  const podeRecortar = temPermissao('ti:cameras:recortar');
  const [params, setParams] = useSearchParams();

  const [catalogo, setCatalogo] = useState(null);
  const [erro, setErro] = useState('');

  const fonte = params.get('ver') || lerLocal(CHAVE_FONTE, 'todos');
  const [layout, setLayout] = useState(() => lerLocal(CHAVE_LAYOUT, LAYOUT_AUTO));
  const [intervalo, setIntervalo] = useState(() => {
    const salvo = Number(lerLocal(CHAVE_INTERVALO, INTERVALO_PADRAO));
    return INTERVALOS.includes(salvo) ? salvo : INTERVALO_PADRAO;
  });

  const [indice, setIndice] = useState(0);
  const [pausado, setPausado] = useState(false);
  const [restante, setRestante] = useState(intervalo);
  const [telaCheia, setTelaCheia] = useState(false);

  const [cameraAberta, setCameraAberta] = useState(null); // { camera, lista }
  const [listaAberta, setListaAberta] = useState(false);
  const [recorte, setRecorte] = useState(null); // { camera } quando aberto
  const [configurando, setConfigurando] = useState(false);

  // Modo mapa: ?mapa= presente (vazio = o primeiro mapa).
  const mapaParam = params.get('mapa');
  const modoMapa = mapaParam !== null;
  const [mapas, setMapas] = useState(null);
  const status = useStatusCameras(modoMapa);

  const palcoRef = useRef(null);
  // A contagem e por HORARIO-ALVO, nao por tique: timer de aba em segundo
  // plano atrasa, e contando tiques o tour ficaria mais lento que o combinado.
  const fimRef = useRef(Date.now() + intervalo * 1000);
  const restanteRef = useRef(intervalo);

  const carregar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cameras/catalogo`);
      const novo = res.data?.data || null;
      // Mantem a MESMA referencia quando nada mudou: trocar o objeto a cada
      // recarga recalcularia as telas a toa.
      setCatalogo((atual) => (JSON.stringify(atual) === JSON.stringify(novo) ? atual : novo));
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar as câmeras.');
    }
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(carregar, RECARREGAR_MS);
    return () => clearInterval(id);
  }, [carregar]);

  const carregarMapas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cameras/mapas`);
      setMapas(res.data?.data?.mapas || []);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar os mapas.');
    }
  }, []);

  useEffect(() => {
    if (!modoMapa) return undefined;
    carregarMapas();
    const id = setInterval(carregarMapas, RECARREGAR_MAPAS_MS);
    return () => clearInterval(id);
  }, [modoMapa, carregarMapas]);

  const renovarRef = useRef(refreshSession);
  renovarRef.current = refreshSession;
  useEffect(() => {
    const id = setInterval(() => renovarRef.current(), RENOVAR_SESSAO_MS);
    return () => clearInterval(id);
  }, []);

  const gravadoresPorId = useMemo(
    () => new Map((catalogo?.gravadores || []).map((g) => [g.id, g])),
    [catalogo],
  );
  const camerasPorId = useMemo(
    () => new Map((catalogo?.cameras || []).map((c) => [c.id, c])),
    [catalogo],
  );
  const fontes = useMemo(() => fontesDoCatalogo(catalogo), [catalogo]);
  const fonteValida = fontes.some((f) => f.chave === fonte) ? fonte : 'todos';
  const { telas, intervalo: intervaloDaFonte } = useMemo(
    () => montarTelas(catalogo, fonteValida, layout),
    [catalogo, fonteValida, layout],
  );
  const total = telas.length;

  const definirRestante = useCallback((segundos) => {
    restanteRef.current = segundos;
    fimRef.current = Date.now() + segundos * 1000;
    setRestante(segundos);
  }, []);

  const avancar = useCallback((passo) => {
    if (total < 1) return;
    setIndice((i) => (((i + passo) % total) + total) % total);
    definirRestante(intervalo);
  }, [total, intervalo, definirRestante]);

  const trocarFonte = (chave) => {
    gravarLocal(CHAVE_FONTE, chave);
    setParams((p) => {
      const novo = new URLSearchParams(p);
      if (chave === 'todos') novo.delete('ver');
      else novo.set('ver', chave);
      return novo;
    }, { replace: true });
    setIndice(0);
    setCameraAberta(null);
  };

  // Entra/sai do modo mapa, ou troca de mapa (id = null sai).
  const irParaMapa = (id) => {
    setParams((p) => {
      const novo = new URLSearchParams(p);
      if (id === null) novo.delete('mapa');
      else novo.set('mapa', id === '' ? '' : String(id));
      return novo;
    }, { replace: true });
    setCameraAberta(null);
  };

  // Tour personalizado traz o proprio tempo de troca.
  useEffect(() => {
    if (intervaloDaFonte) {
      setIntervalo(intervaloDaFonte);
      definirRestante(intervaloDaFonte);
    }
  }, [fonteValida, intervaloDaFonte, definirRestante]);

  // Mudou a fonte ou o layout: recomeca da primeira tela, com o tempo do tour
  // (se ele tiver um) ou o escolhido na tela.
  useEffect(() => {
    setIndice(0);
    definirRestante(intervaloDaFonte || intervalo);
  }, [fonteValida, layout]); // eslint-disable-line react-hooks/exhaustive-deps

  // Relogio do tour. Para com a camera aberta sozinha (o tour fica por baixo).
  const tourParado = pausado || !!cameraAberta || modoMapa || total < 2;
  useEffect(() => {
    if (tourParado) return undefined;
    fimRef.current = Date.now() + restanteRef.current * 1000;
    const id = setInterval(() => {
      const falta = Math.ceil((fimRef.current - Date.now()) / 1000);
      if (falta <= 0) {
        avancar(1);
      } else if (falta !== restanteRef.current) {
        restanteRef.current = falta;
        setRestante(falta);
      }
    }, 250);
    return () => clearInterval(id);
  }, [tourParado, avancar]);

  const trocarIntervalo = (segundos) => {
    setIntervalo(segundos);
    definirRestante(segundos);
    gravarLocal(CHAVE_INTERVALO, segundos);
  };

  const trocarLayout = (id) => {
    setLayout(id);
    gravarLocal(CHAVE_LAYOUT, id);
  };

  const alternarTelaCheia = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen();
    else palcoRef.current?.requestFullscreen?.().catch(() => { /* navegador recusou */ });
  }, []);

  useEffect(() => {
    const aoMudar = () => setTelaCheia(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', aoMudar);
    return () => document.removeEventListener('fullscreenchange', aoMudar);
  }, []);

  // Atalhos do tour: setas trocam de tela, espaco pausa, F tela cheia. A
  // camera aberta sozinha tem os proprios atalhos.
  const modalAberto = !!recorte || configurando;
  useEffect(() => {
    if (modalAberto || cameraAberta) return undefined;
    const aoTeclar = (e) => {
      const tag = e.target?.tagName || '';
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(tag)) return;
      // Espaco num botao focado ja e o clique dele; tratar aqui acionaria duas vezes.
      if (e.key === ' ' && tag === 'BUTTON') return;
      if (e.key === 'f' || e.key === 'F') { alternarTelaCheia(); return; }
      if (modoMapa) return;
      if (e.key === 'ArrowRight') avancar(1);
      else if (e.key === 'ArrowLeft') avancar(-1);
      else if (e.key === ' ') { e.preventDefault(); setPausado((p) => !p); }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [modalAberto, cameraAberta, modoMapa, avancar, alternarTelaCheia]);

  // Modais vivem fora do palco; em tela cheia ficariam invisiveis.
  const sairDaTelaCheia = () => {
    if (document.fullscreenElement) document.exitFullscreen();
  };

  const abrirRecorte = (camera = null) => {
    sairDaTelaCheia();
    setRecorte({ camera });
  };

  const atual = total ? telas[indice % total] : null;
  const proxima = total > 1 ? telas[(indice + 1) % total] : null;
  const precarregando = !tourParado && proxima && restante <= PRE_CARGA_S;

  // A proxima entra DEPOIS da atual na lista: quando ela assume, o React so
  // remove a anterior e mantem a que ja estava tocando (mesma key), sem
  // remontar nenhum player.
  const camadas = [];
  if (atual) camadas.push(atual);
  if (precarregando && proxima.id !== atual.id) camadas.push(proxima);

  // Telas que misturam equipamentos mostram "Loja 03 · Caixa 01" no tile.
  const misturaEquipamentos = fonteValida !== 'todos';

  return (
    <div className="cameras-pagina">
      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}
      {catalogo && !catalogo.gateway_ok && catalogo.cameras.length > 0 ? (
        <div className="feedback-banner feedback-banner--warning">
          O gateway de vídeo (go2rtc) não está respondendo — as câmeras não vão abrir até ele voltar.
        </div>
      ) : null}

      <div className="cameras-palco" ref={palcoRef}>
        <div className="cameras-barra">
          <div className="cameras-barra__titulo">
            <i className={`bi ${modoMapa ? 'bi-map-fill' : 'bi-camera-video-fill'}`} />
            <strong>{modoMapa ? 'Mapa das câmeras' : atual ? atual.titulo : 'Câmeras'}</strong>
            {!modoMapa && atual && atual.partes > 1 && (
              <span className="cameras-barra__sub">parte {atual.parte}/{atual.partes}</span>
            )}
          </div>

          <div className="cameras-barra__controles">
            <button
              type="button"
              className={`cameras-barra__btn ${modoMapa ? 'cameras-barra__btn--ativo' : ''}`}
              onClick={() => irParaMapa(modoMapa ? null : '')}
              title={modoMapa ? 'Voltar ao tour' : 'Ver as câmeras no mapa da loja'}
              aria-pressed={modoMapa}
            >
              <i className={`bi ${modoMapa ? 'bi-grid-3x3-gap-fill' : 'bi-map'}`} />
              <span>{modoMapa ? 'Tour' : 'Mapa'}</span>
            </button>

            {!modoMapa && (
              <>
                <select
                  className="cameras-barra__select"
                  value={fonteValida}
                  onChange={(e) => trocarFonte(e.target.value)}
                  title="O que exibir"
                  aria-label="O que exibir"
                >
                  {fontes.map((f) => (
                    <option key={f.chave} value={f.chave}>
                      {f.tipo === 'tour' ? 'Tour: ' : f.tipo === 'grupo' ? 'Grupo: ' : ''}{f.rotulo}
                    </option>
                  ))}
                </select>
                <select
                  className="cameras-barra__select"
                  value={layout}
                  onChange={(e) => trocarLayout(e.target.value)}
                  title="Layout"
                  aria-label="Layout"
                >
                  <option value={LAYOUT_AUTO}>Layout automático</option>
                  {LAYOUTS.map((l) => <option key={l.id} value={l.id}>{l.rotulo}</option>)}
                </select>

                {total > 1 && (
                  <>
                    <span className="cameras-barra__sub">
                      {(indice % total) + 1}/{total}
                      {pausado ? ' · pausado' : ` · ${restante}s`}
                    </span>
                    <button type="button" className="cameras-barra__btn" onClick={() => avancar(-1)} title="Tela anterior (←)">
                      <i className="bi bi-skip-start-fill" />
                    </button>
                    <button
                      type="button"
                      className="cameras-barra__btn"
                      onClick={() => setPausado((p) => !p)}
                      title={pausado ? 'Retomar o tour (espaço)' : 'Pausar nesta tela (espaço)'}
                      aria-pressed={pausado}
                    >
                      <i className={`bi ${pausado ? 'bi-play-fill' : 'bi-pause-fill'}`} />
                    </button>
                    <button type="button" className="cameras-barra__btn" onClick={() => avancar(1)} title="Próxima tela (→)">
                      <i className="bi bi-skip-end-fill" />
                    </button>
                    <select
                      className="cameras-barra__select"
                      value={intervalo}
                      onChange={(e) => trocarIntervalo(Number(e.target.value))}
                      title="Tempo em cada tela"
                    >
                      {[...new Set([...INTERVALOS, intervalo])].sort((a, b) => a - b)
                        .map((s) => <option key={s} value={s}>{s}s</option>)}
                    </select>
                  </>
                )}
              </>
            )}

            <button
              type="button"
              className={`cameras-barra__btn ${listaAberta ? 'cameras-barra__btn--ativo' : ''}`}
              onClick={() => setListaAberta((v) => !v)}
              title="Lista de câmeras"
              disabled={!catalogo?.cameras.length}
            >
              <i className="bi bi-list-ul" />
              <span>Câmeras</span>
            </button>
            {podeRecortar && (
              <button type="button" className="cameras-barra__btn" onClick={() => abrirRecorte(null)} title="Recortar e baixar gravações">
                <i className="bi bi-scissors" />
                <span>Gravações</span>
              </button>
            )}
            {podeConfigurar && (
              <button
                type="button"
                className="cameras-barra__btn"
                onClick={() => { sairDaTelaCheia(); setConfigurando(true); }}
                title="Equipamentos, câmeras, grupos, tours e mapas"
              >
                <i className="bi bi-gear-fill" />
                <span>Configurar</span>
              </button>
            )}
            <button
              type="button"
              className="cameras-barra__btn"
              onClick={alternarTelaCheia}
              title={telaCheia ? 'Sair da tela cheia (F)' : 'Tela cheia (F)'}
            >
              <i className={`bi ${telaCheia ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`} />
            </button>
          </div>

          {total > 1 && !tourParado && (
            <div
              className="cameras-barra__progresso"
              style={{ width: `${Math.min(100, ((intervalo - restante + 1) / intervalo) * 100)}%` }}
            />
          )}
        </div>

        <div className="cameras-corpo">
          <div className="cameras-telas">
            {!catalogo || (modoMapa && !mapas) ? (
              <div className="cameras-vazio">
                <div className="screen-loader__spinner" />
                <span>{modoMapa ? 'Carregando mapas…' : 'Carregando câmeras…'}</span>
              </div>
            ) : modoMapa ? (
              <MapaCameras
                mapas={mapas}
                mapaId={Number(mapaParam) || null}
                onTrocarMapa={(id) => irParaMapa(id)}
                camerasPorId={camerasPorId}
                gravadoresPorId={gravadoresPorId}
                status={status}
                podeConfigurar={podeConfigurar}
                onAbrirCamera={(camera, lista) => setCameraAberta({ camera, lista })}
              />
            ) : total === 0 ? (
              <div className="cameras-vazio">
                <i className="bi bi-camera-video-off" style={{ fontSize: 40, opacity: 0.5 }} />
                <strong>Nenhuma câmera para exibir</strong>
                <span>
                  {podeConfigurar
                    ? 'Cadastre os equipamentos em "Configurar" para começar.'
                    : 'Peça para a equipe de TI cadastrar os equipamentos.'}
                </span>
              </div>
            ) : (
              camadas.map((tela) => (
                <Mosaico
                  key={tela.id}
                  tela={tela}
                  ativa={tela.id === atual.id}
                  gravadoresPorId={gravadoresPorId}
                  mostrarEquipamento={misturaEquipamentos}
                  onAbrirCamera={(camera) => setCameraAberta({ camera, lista: tela.cameras })}
                />
              ))
            )}

            {cameraAberta && (
              <CameraUnica
                camera={cameraAberta.camera}
                cameras={cameraAberta.lista}
                gravadoresPorId={gravadoresPorId}
                onFechar={() => setCameraAberta(null)}
                onTrocar={(camera) => setCameraAberta((a) => ({ ...a, camera }))}
                onRecortar={podeRecortar ? (camera) => abrirRecorte(camera) : null}
              />
            )}
          </div>

          {listaAberta && catalogo && (
            <ListaCameras
              catalogo={catalogo}
              onAbrir={(camera) => setCameraAberta({ camera, lista: catalogo.cameras })}
              onFechar={() => setListaAberta(false)}
            />
          )}
        </div>
      </div>

      {recorte && catalogo && (
        <RecortesModal
          catalogo={catalogo}
          cameraInicial={recorte.camera}
          onClose={() => setRecorte(null)}
        />
      )}
      {configurando && (
        <ConfigCameras
          onClose={(alterou) => {
            setConfigurando(false);
            if (alterou) {
              carregar();
              if (modoMapa) carregarMapas();
            }
          }}
        />
      )}
    </div>
  );
}
